import { CompanyState } from '@/stores/Company/state'

export type CompanyGetters = {
  [K in keyof CompanyState]: (state: CompanyState) => CompanyState[K]
} & {
  gicsClassifications: (state: CompanyState, getters: any) => string[]
  gicsByClassification: (state: CompanyState, getters: any) => any[]
  isRAC: (state: CompanyState) => boolean | undefined
  isRACYellow: (state: CompanyState) => boolean
  isRACRed: (state: CompanyState) => boolean
  isRACGreen: (state: CompanyState) => boolean
  racYellowFilter: (state: CompanyState) => boolean | undefined
  racGreenFilter: (state: CompanyState) => boolean | undefined
  racRedFilter: (state: CompanyState) => boolean | undefined
}

// Generic type that maps state keys to getter functions, augmented with additional computed properties
export type StateGettersWithAugmentation<TState, TAugmentation = Record<string, unknown>> = {
  [K in keyof TState]: (state: TState) => TState[K]
} & TAugmentation

// Specific type for CompanyState with gicsClassifications augmentation
export type AugmentedCompanyGetters = StateGettersWithAugmentation<
  CompanyState,
  {
    gicsClassifications: (state: CompanyState, getters: any) => string[]
    gicsByClassification: (state: CompanyState, getters: any) => any[]
    isRAC: (state: CompanyState) => boolean | undefined
    isRACYellow: (state: CompanyState) => boolean
    isRACRed: (state: CompanyState) => boolean
    isRACGreen: (state: CompanyState) => boolean
    racYellowFilter: (state: CompanyState) => boolean | undefined
    racGreenFilter: (state: CompanyState) => boolean | undefined
    racRedFilter: (state: CompanyState) => boolean | undefined
  }
>

export default {
  companyList: (state: CompanyState) => state.companyList,
  newIssuesOnly: (state: CompanyState) => state.newIssuesOnly,
  companyListFetching: (state: CompanyState) => state.companyListFetching,
  companyMap: (state: CompanyState) => state.companyMap,
  currentCompany: (state: CompanyState) => state.currentCompany,
  companyWatcher: (state: CompanyState) => state.companyWatcher,
  gicsList: (state: CompanyState) => state.gicsList,
  gicsClassifications: (state: CompanyState) => {
    return state.gicsList.reduce((acc, gics) => {
      if (!acc.includes(gics.classification)) {
        acc.push(gics.classification)
      }
      return acc
    }, [])
  },
  gicsByClassification: (state: CompanyState) => {
    const { gicsList } = state
    const sectors = gicsList.filter((item) => item.code.length < 4)
    const industryGroups = gicsList.filter((item) => item.code.length < 6)
    const industries = gicsList.filter((item) => item.code.length < 8)
    const subIndustries = gicsList.filter((item) => item.code.length >= 8)

    return [...new Set([...sectors, ...industryGroups, ...industries, ...subIndustries])]
  },
  cashSourceList: (state: CompanyState) => state.cashSourceList,
  terminalNavigationVisible: (state: CompanyState) => state.terminalNavigationVisible,
  companyUiInfo: (state: CompanyState) => state.companyUiInfo,
  historyList: (state: CompanyState) => state.historyList,
  mostViewedList: (state: CompanyState) => state.mostViewedList,
  nextReportingList: (state: CompanyState) => state.nextReportingList,
  lastReportedList: (state: CompanyState) => state.lastReportedList,
  companyListCount: (state: CompanyState) => state.companyListCount,
  searchParam: (state: CompanyState) => state.searchParam,
  companySorting: (state: CompanyState) => state.companySorting,
  memoStructureList: (state: CompanyState) => state.memoStructureList,
  companyListError: (state: CompanyState) => state.companyListError,
  companyListPage: (state: CompanyState) => state.companyListPage,
  searchSuggestions: (state: CompanyState) => state.searchSuggestions,
  isRAC: (state: CompanyState) => state.currentCompany?.restrictedAccess,
  isRACYellow: (state: CompanyState) => state.currentCompany?.restrictedAccessState === 'yellow',
  isRACRed: (state: CompanyState) => state.currentCompany?.restrictedAccessState === 'red',
  isRACGreen: (state: CompanyState) => state.currentCompany?.restrictedAccessState === 'green',
  racYellowFilter: (state: CompanyState) => state.racFilter.yellow,
  racGreenFilter: (state: CompanyState) => state.racFilter.green,
  racRedFilter: (state: CompanyState) => state.racFilter.red,
  earningsSummary: (state: CompanyState) => state.earningsSummary,
  showWatchlistTooltip: (state: CompanyState) => state.showWatchlistTooltip,
  recentDocuments: (state: CompanyState) => state.recentDocuments,
  racFilter: (state: CompanyState) => state.racFilter,
  filter: (state: CompanyState) => state.filter,
} satisfies CompanyGetters
